import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Platform, Pressable, StyleSheet, View, ViewStyle } from "react-native"

import { KeyboardEvents } from "react-native-keyboard-controller"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import type { ControlsConfig, PlaybackControlsConfig } from "src/components/SidekickView/types"
import LinearGradient from "src/components/LinearGradient"
import { NavigationControls } from "src/components/SidekickView/NavigationControls"
import {
  PlaybackLeftControls,
  PlaybackScrubber,
} from "src/components/SidekickView/PlaybackControls"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { useWebLayoutConstraints } from "src/hooks/useWebLayoutConstraints"
import { ThemedStyle } from "src/theme"
import { isAvatarGestureActive } from "src/utils/avatarGestureGuard"
import { hexToRgba } from "src/utils/colorUtils"

/**
 * Props for the MediaPlayerShell component.
 *
 * MediaPlayerShell is a presentation-only layout shell for media-viewing
 * experiences (video player, audio player, web browser, pdf viewer, image
 * viewer, and the unauthenticated media viewer). It is intentionally free of
 * sidekick / chat / microphone coupling so it can be used in both
 * authenticated and unauthenticated contexts.
 *
 * All sidekick-specific behavior (avatar, button cluster, talk/type,
 * progress overlay) is supplied by the caller through slot props
 * (avatarSlot, bottomSlot, progressOverlay, topRightAccessory, etc.).
 */
export interface MediaPlayerShellProps {
  /** Main media content. Rendered absoluteFill in the content layer. */
  children: React.ReactNode
  /**
   * Optional avatar overlay. Rendered in the avatar region (above content,
   * below the controls section). Caller is responsible for the avatar's
   * visibility / mode logic.
   */
  avatarSlot?: React.ReactNode
  /**
   * Optional progress overlay (e.g. SidekickProgress). Positioned absolutely
   * just above the controls section so it doesn't affect the content area's
   * paddingBottom measurement.
   */
  progressOverlay?: React.ReactNode
  /**
   * Top-right floating accessory (typically a close button). Fades with
   * controlsOpacity and is gated by controlsVisible.
   */
  topRightAccessory?: React.ReactNode
  /**
   * Top-center floating accessory (typically a title). Fades with
   * controlsOpacity and is gated by controlsVisible.
   */
  topCenterAccessory?: React.ReactNode
  /**
   * Bottom action area (e.g. ButtonCluster, Talk/Type pill, custom buttons).
   * Rendered as the right-most cell of the controls row. The shell handles
   * outer padding (insets) so callers shouldn't add their own.
   */
  bottomSlot?: React.ReactNode
  /**
   * Optional element rendered to the left of the bottomSlot in the controls
   * row (e.g. play/pause button, library button, navigation controls).
   * If a controlsConfig of type "playback" or "navigation" is provided and
   * leftAccessory is not, the shell will render the appropriate built-in
   * left controls.
   */
  leftAccessory?: React.ReactNode
  /**
   * When true, suppress the built-in left controls (play/pause for playback,
   * back/forward for navigation) even when controlsConfig provides them.
   * Useful when the caller wants the bottomSlot to span the full width of
   * the controls row (e.g. centering a speech-input button) while still
   * having the scrubber render above. Has no effect if leftAccessory is set.
   */
  suppressBuiltInLeftControls?: boolean
  /**
   * Playback / navigation controls config. When type === "playback" the shell
   * renders a PlaybackScrubber row above the bottom controls row, and (unless
   * leftAccessory is set) the play/pause button as the left accessory.
   */
  controlsConfig?: ControlsConfig
  /**
   * External Animated.Value driving the opacity of the topRightAccessory,
   * topCenterAccessory, scrubber row and bottom controls row. Useful for
   * fading controls in/out as playback toggles.
   */
  controlsOpacity?: Animated.Value | Animated.AnimatedInterpolation<number>
  /**
   * Whether the controls / accessories are interactive. When false, all
   * pointer events on those layers are disabled.
   */
  controlsVisible?: boolean
  /**
   * Called when the user taps the content area. The shell renders an
   * invisible Pressable overlay above the content (but below avatar/controls)
   * to capture taps without intercepting button presses.
   */
  onContentPress?: () => void
  /**
   * When true (default) the container is dark-themed for media playback.
   * When false, the theme background color is used (matches audio-player).
   */
  darkBackground?: boolean
  /**
   * When true (default), the content area reserves bottom padding equal to
   * the measured controls height so the content doesn't slip behind the
   * controls. Set to false for fully edge-to-edge content (e.g. video).
   */
  reserveSpaceForControls?: boolean
  /**
   * Whether the avatar backdrop (a black scrim) should be rendered behind
   * the avatar slot. Caller controls when it appears via the
   * avatarBackdropVisible prop.
   */
  avatarBackdrop?: boolean
  /**
   * When true, the avatar backdrop fades to opaque. Has no effect unless
   * avatarBackdrop is also true.
   */
  avatarBackdropVisible?: boolean
  /** Optional style override for the outer container. */
  style?: ViewStyle
  /**
   * Extra bottom padding (in addition to the safe-area inset) applied to the
   * controls row. Useful on Android in compact button-cluster mode when the
   * nav bar is hidden — insets.bottom drops to 0 and the row would otherwise
   * collide with the gesture area.
   */
  controlsExtraBottomPadding?: number
  /**
   * Called when the measured height of the controls section changes (or
   * 0 when the controls are hidden, e.g. while the keyboard is visible).
   * Useful for callers that need to position descendants (e.g. an avatar
   * overlay) relative to the controls.
   */
  onControlsHeightChange?: (height: number) => void
  /**
   * Height (in px) of caller-supplied interactive content that lives at the
   * bottom of the children area — typically a docked text input that sits
   * above the keyboard. When > 0, the tap-capture overlay shrinks so the
   * region occupied by that content (plus the reserved controls/keyboard
   * padding) is excluded from `onContentPress`. Without this, taps on a
   * docked input's send button would otherwise be intercepted as
   * play/pause taps on screens that pass `onContentPress`.
   *
   * Has no effect when `onContentPress` is not provided (no overlay is
   * rendered in that case).
   */
  bottomContentInset?: number
  /**
   * When true, the controls section remains visible while the keyboard is
   * open (instead of being hidden). The foreground layer adds bottom padding
   * equal to the keyboard height so the controls appear above the keyboard.
   * Use this when the controls row contains an inline text input (e.g.
   * CompositeInputCluster) so the cluster stays accessible while typing.
   */
  keepControlsOnKeyboard?: boolean
  /**
   * When true (default), renders the fade gradient above the controls bar background.
   * Set to false to suppress the gradient while keeping the reserved space.
   * Has no effect when reserveSpaceForControls is false.
   */
  showControlsGradient?: boolean
}

const BACKDROP_DURATION_MS = 300

/**
 * MediaPlayerShell - Pure presentation layout for media-viewing screens.
 *
 * Layout (top to bottom):
 *  - Container (dark or themed background, safe-area aware)
 *  - Content layer (children, absoluteFill, padded for top inset and
 *    optionally controls height)
 *  - Tap overlay (invisible Pressable when onContentPress is provided)
 *  - Avatar region (avatarSlot, flex:1, top-aligned, horizontally centered)
 *  - Progress overlay (absolute, just above controls section)
 *  - Controls section:
 *      - Scrubber row (when controlsConfig.type === "playback")
 *      - Button row: [leftAccessory or built-in controls] [bottomSlot]
 *  - Top-right accessory (absolute)
 *  - Top-center accessory (absolute)
 *
 * The shell never imports sidekick-specific contexts. It only depends on
 * theme + safe-area + status-bar compensation, which are available in both
 * authenticated and unauthenticated trees.
 */
export function MediaPlayerShell({
  children,
  avatarSlot,
  progressOverlay,
  topRightAccessory,
  topCenterAccessory,
  bottomSlot,
  leftAccessory,
  suppressBuiltInLeftControls = false,
  controlsConfig,
  controlsOpacity: externalControlsOpacity,
  controlsVisible = true,
  onContentPress,
  darkBackground = true,
  reserveSpaceForControls = true,
  avatarBackdrop = false,
  avatarBackdropVisible = false,
  style,
  controlsExtraBottomPadding = 0,
  onControlsHeightChange,
  bottomContentInset = 0,
  keepControlsOnKeyboard = false,
  showControlsGradient = true,
}: MediaPlayerShellProps) {
  const {
    themed,
    theme: { colors },
  } = useAppTheme()
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { canvasWidth } = useWebLayoutConstraints()

  const [controlsHeight, setControlsHeight] = useState(0)
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  const internalControlsOpacity = useRef(new Animated.Value(1)).current
  const controlsOpacity = externalControlsOpacity ?? internalControlsOpacity

  const backdropOpacity = useRef(new Animated.Value(0)).current

  /**
   * Tap-to-toggle handler with an avatar-gesture guard. The avatar's pointer
   * handlers (in `useWebAvatarDrag`) flip a module-level flag during a tap
   * and for a short grace window after `pointerup`. If the synthetic click
   * that the browser dispatches after a tap on the minimized avatar somehow
   * escapes the document-level capture-phase suppressor (e.g. due to event
   * delegation timing changes after re-renders triggered by play/pause
   * state), this guard makes sure the tap-capture Pressable still bails out
   * instead of toggling playback.
   *
   * On native platforms the flag is never flipped, so this is a no-op.
   */
  const handleContentPress = useCallback(() => {
    if (Platform.OS === "web" && isAvatarGestureActive()) return
    onContentPress?.()
  }, [onContentPress])

  useEffect(() => {
    if (!avatarBackdrop) {
      Animated.timing(backdropOpacity, {
        toValue: 0,
        duration: BACKDROP_DURATION_MS,
        useNativeDriver: true,
      }).start()
      return
    }

    Animated.timing(backdropOpacity, {
      toValue: avatarBackdropVisible ? 1 : 0,
      duration: BACKDROP_DURATION_MS,
      useNativeDriver: true,
    }).start()
  }, [avatarBackdrop, avatarBackdropVisible, backdropOpacity])

  // Delayed scrubber-show timer. We keep a ref so it can be cancelled if the
  // keyboard shows again before the delay expires.
  const scrubberShowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // KeyboardEvents fires keyboardWillShow/keyboardWillHide at the START of
    // the animation on both iOS and Android, eliminating the ~300 ms delay
    // from the old keyboardDidShow approach on Android.
    const showSub = KeyboardEvents.addListener("keyboardWillShow", (e) => {
      // Cancel any pending scrubber-show if the keyboard pops back up quickly.
      if (scrubberShowTimer.current) {
        clearTimeout(scrubberShowTimer.current)
        scrubberShowTimer.current = null
      }
      setIsKeyboardVisible(true)
      setKeyboardHeight(e.height)
    })
    const hideSub = KeyboardEvents.addListener("keyboardWillHide", () => {
      // Remove the padding immediately so the controls track the keyboard down.
      setKeyboardHeight(0)
      // Delay the scrubber reappearance until the pill-collapse LayoutAnimation
      // (300 ms) has finished. Showing the scrubber immediately fires a React
      // re-render that re-commits the shadow-tree height as 112 px (the last
      // committed value), overriding the collapse and leaving the pill stuck.
      scrubberShowTimer.current = setTimeout(() => {
        scrubberShowTimer.current = null
        setIsKeyboardVisible(false)
      }, 350)
    })
    return () => {
      showSub.remove()
      hideSub.remove()
      if (scrubberShowTimer.current) clearTimeout(scrubberShowTimer.current)
    }
  }, [])

  const handleControlsLayout = useCallback((event: any) => {
    const { height } = event.nativeEvent.layout
    setControlsHeight(height)
  }, [])

  // Report effective controls height. When keepControlsOnKeyboard is true the
  // controls remain visible, so report the actual height rather than 0.
  const effectiveControlsHeight = isKeyboardVisible && !keepControlsOnKeyboard ? 0 : controlsHeight
  useEffect(() => {
    onControlsHeightChange?.(effectiveControlsHeight)
  }, [effectiveControlsHeight, onControlsHeightChange])

  const renderBuiltInLeftControls = useMemo(() => {
    if (suppressBuiltInLeftControls) return null
    if (!controlsConfig) return null
    switch (controlsConfig.type) {
      case "playback":
        return (
          <PlaybackLeftControls
            isPlaying={controlsConfig.isPlaying}
            onTogglePlay={controlsConfig.onTogglePlay}
          />
        )
      case "navigation":
        return <NavigationControls config={controlsConfig} />
      default:
        return null
    }
  }, [controlsConfig, suppressBuiltInLeftControls])

  const effectiveLeftAccessory = leftAccessory ?? renderBuiltInLeftControls
  const hasLeftAccessory = !!effectiveLeftAccessory

  const isPlaybackConfig = controlsConfig?.type === "playback"
  const isWebFullscreen =
    Platform.OS === "web" &&
    isPlaybackConfig &&
    !!(controlsConfig as PlaybackControlsConfig).isFullscreen

  // Distance from the screen bottom to the top of the controls gap.
  // When the keyboard is up (and controls stay above it), we add keyboardHeight
  // so the gradient and content padding both track the controls as they rise.
  const controlsOffset =
    isKeyboardVisible && keepControlsOnKeyboard
      ? keyboardHeight + controlsHeight + 16
      : controlsHeight + 16

  const gradientBottom =
    reserveSpaceForControls && (!isKeyboardVisible || keepControlsOnKeyboard) ? controlsOffset : 0

  const contentPaddingBottom = !reserveSpaceForControls
    ? 0
    : isKeyboardVisible && !keepControlsOnKeyboard
      ? keyboardHeight
      : controlsOffset

  // The tap-capture overlay defaults to fullscreen. When the caller renders
  // bottom-anchored interactive content (e.g. a TypingChatInputDock above the
  // keyboard), we shrink the overlay to clear that region. The dock sits at
  // the bottom of the content area, so its bottom edge is `contentPaddingBottom`
  // above the screen bottom and its top edge is `contentPaddingBottom +
  // bottomContentInset` above the screen bottom — which is exactly the
  // overlay's bottom inset we need.
  const tapOverlayBottomInset =
    bottomContentInset > 0 ? contentPaddingBottom + bottomContentInset : 0

  return (
    <View
      style={[
        styles.container,
        darkBackground && themed($darkBackground),
        style,
        isWebFullscreen && (webFullscreenStyle as ViewStyle),
      ]}
    >
      {/* Content layer.
          `justifyContent: "flex-end"` anchors any in-flow children to the
          bottom of the padded area (i.e. just above the keyboard or the
          reserved controls section). This matters for callers like
          SidekickView that render an inline TypingChatInputDock as a sibling
          of the host screen's children: when the host's children are
          `absoluteFill` (e.g. the video/audio players' VideoView/AudioView),
          the dock would otherwise be the only in-flow item and would land at
          the top of the column. Screens whose children include a `flex: 1`
          wrapper (chat, menus) are unaffected — flex:1 still fills the
          column, so the dock continues to sit immediately below it. */}
      <View
        style={[
          StyleSheet.absoluteFill,
          styles.contentLayer,
          {
            paddingTop: effectiveTopInset,
            paddingBottom: contentPaddingBottom,
          },
        ]}
        pointerEvents="box-none"
      >
        {children}
      </View>

      {/* Foreground layer: backdrop, tap-capture, avatar, progress, controls.
          The tap-capture lives INSIDE the foreground layer (rather than as an
          outer sibling) so it shares the avatar/controls stacking context.
          Otherwise, when the foreground is `pointerEvents="box-none"` (which
          maps to `pointer-events: none` on the layer itself), browsers can
          fall through the foreground entirely on hit-tests that land above an
          interactive descendant — sending taps meant for the minimized avatar
          to whatever Pressable sits below it. */}
      <View
        style={[
          styles.foregroundLayer,
          keepControlsOnKeyboard && isKeyboardVisible && { paddingBottom: keyboardHeight },
        ]}
        pointerEvents="box-none"
      >
        {/* Avatar backdrop scrim */}
        {avatarBackdrop && (
          <Animated.View
            style={[StyleSheet.absoluteFill, styles.avatarBackdrop, { opacity: backdropOpacity }]}
            pointerEvents="none"
          />
        )}

        {/* Tap-to-toggle overlay (below avatar/controls in z-order). When
            `bottomContentInset` is set, shrink from the bottom so taps on the
            caller's docked content (e.g. a TypingChatInputDock send button)
            aren't intercepted as media play/pause taps. */}
        {onContentPress && (
          <Pressable
            style={[
              StyleSheet.absoluteFill,
              styles.tapCaptureOverlay,
              tapOverlayBottomInset > 0 && { bottom: tapOverlayBottomInset },
            ]}
            onPress={handleContentPress}
            pointerEvents="auto"
          />
        )}

        {/* Avatar region */}
        <View style={styles.avatarRegion} pointerEvents="box-none">
          {avatarSlot}
        </View>

        {/* Progress overlay row, anchored above the controls section.
            Hidden while keyboard is visible on screens where the controls
            disappear (keepControlsOnKeyboard=false) because controlsHeight
            drops to 0 and the overlay would land at the screen bottom.
            When keepControlsOnKeyboard=true the controls stay above the
            keyboard so controlsHeight is valid and the overlay can show. */}
        {progressOverlay && (!isKeyboardVisible || keepControlsOnKeyboard) && (
          <View
            style={[styles.progressOverlayRow, { bottom: controlsHeight }]}
            pointerEvents="none"
          >
            {progressOverlay}
          </View>
        )}

        {/* Controls background gradient — sits at the top of the reserved controls area.
            When reserveSpaceForControls is true, contentPaddingBottom is the distance from
            the screen bottom to where the scrollable content ends, so anchoring the gradient
            there places its bottom edge right at the top of the solid background gap. Falls
            back to controlsHeight when no space is reserved (content extends under controls). */}
        {reserveSpaceForControls &&
          showControlsGradient &&
          (!isKeyboardVisible || keepControlsOnKeyboard) && (
            <LinearGradient
              colors={[hexToRgba(colors.background, 0), hexToRgba(colors.background, 1)]}
              style={[styles.controlsBackgroundGradient, { bottom: gradientBottom }]}
              pointerEvents="none"
            />
          )}

        {/* Controls section */}
        {(!isKeyboardVisible || keepControlsOnKeyboard) && (
          <View
            style={styles.controlsSection}
            pointerEvents="box-none"
            onLayout={handleControlsLayout}
          >
            {isPlaybackConfig && !isKeyboardVisible && (
              <Animated.View
                style={[
                  styles.scrubberRow,
                  {
                    paddingLeft: insets.left + 16,
                    paddingRight: insets.right + 16,
                    opacity: controlsOpacity,
                  },
                ]}
                pointerEvents={controlsVisible ? "box-none" : "none"}
              >
                <PlaybackScrubber config={controlsConfig} />
              </Animated.View>
            )}

            {(hasLeftAccessory || bottomSlot) && (
              <View
                style={[
                  styles.buttonRow,
                  {
                    // When the keyboard is open it covers the home-indicator safe
                    // area, so insets.bottom would add ~34 px of dead space between
                    // the input cluster and the keyboard on iPhone. Use a minimal
                    // gap instead and restore the full inset once the keyboard hides.
                    paddingBottom:
                      isKeyboardVisible && keepControlsOnKeyboard
                        ? 8
                        : insets.bottom + 8 + controlsExtraBottomPadding,
                    paddingLeft: insets.left + 16,
                    paddingRight: insets.right + 16,
                    // Cap the controls row at the chat-screen canvas width so the
                    // cluster matches the chat screen's size in full-width contexts
                    // (content library, etc.). The row is centered when it's narrower
                    // than its container. width: "100%" grows up to the maxWidth limit.
                    width: "100%",
                    maxWidth: canvasWidth,
                    alignSelf: "center",
                  },
                ]}
              >
                {hasLeftAccessory && (
                  <Animated.View
                    style={[styles.leftAccessoryWrapper, { opacity: controlsOpacity }]}
                    pointerEvents={controlsVisible ? "auto" : "none"}
                  >
                    {effectiveLeftAccessory}
                  </Animated.View>
                )}

                {/* Spacer: right-aligns the bottom slot unless keepControlsOnKeyboard
                    (composite cluster) is active — in that case the slot expands to
                    fill the remaining space beside the left accessory instead. */}
                {hasLeftAccessory && bottomSlot && !keepControlsOnKeyboard && (
                  <View style={styles.flexSpacer} />
                )}

                {bottomSlot && (
                  <Animated.View
                    style={[
                      styles.bottomSlotWrapper,
                      (!hasLeftAccessory || keepControlsOnKeyboard) && styles.bottomSlotFullWidth,
                      { opacity: controlsOpacity },
                    ]}
                    pointerEvents={controlsVisible ? "box-none" : "none"}
                  >
                    {bottomSlot}
                  </Animated.View>
                )}
              </View>
            )}
          </View>
        )}
      </View>

      {/* Top-center accessory (e.g. login button) — fades and disables with controls.
          A flex row with side-slot mirrors keeps the content truly centered. */}
      {topCenterAccessory && (
        <Animated.View
          style={[
            styles.topAccessoriesRow,
            {
              top: effectiveTopInset + 12,
              left: insets.left + 12,
              right: insets.right + 12,
              opacity: controlsOpacity,
            },
            Platform.OS === "web" && ({ position: "fixed" } as any),
          ]}
          pointerEvents={controlsVisible ? "box-none" : "none"}
        >
          <View style={styles.topRowSideSlot} />
          <View style={styles.topRowCenter}>{topCenterAccessory}</View>
          <View style={styles.topRowSideSlot} />
        </Animated.View>
      )}

      {/* Top-right accessory (e.g. close button) — always visible and interactive
          so the user can exit regardless of playback state or controls visibility. */}
      {topRightAccessory && (
        <View
          style={[
            styles.topRightAccessory,
            {
              top: effectiveTopInset + 12,
              right: insets.right + 12,
            },
            Platform.OS === "web" && ({ position: "fixed" } as any),
          ]}
          pointerEvents="auto"
        >
          {topRightAccessory}
        </View>
      )}
    </View>
  )
}

const $darkBackground: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
})

// Applied on web when isFullscreen is true — escapes the 720 px LayoutWrapper
// constraint by using fixed positioning anchored to the viewport.
const webFullscreenStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9000,
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentLayer: {
    justifyContent: "flex-end",
  },
  tapCaptureOverlay: {
    // No zIndex: lets the avatar region (zIndex: 10000) and the progress /
    // controls sections (which come later in document order) sit above the
    // tap-capture layer in the foreground's stacking context.
  },
  foregroundLayer: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: "column",
    zIndex: 1000,
  },
  avatarBackdrop: {
    backgroundColor: "black",
  },
  avatarRegion: {
    flex: 1,
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    zIndex: 10000,
  },
  controlsSection: {
    flexDirection: "column",
    // Sit above the tap-capture Pressable (which is also inside the foreground
    // with no zIndex) so taps on play/pause, scrubber, etc. aren't swallowed.
    zIndex: 1,
  },
  progressOverlayRow: {
    position: "absolute",
    left: 16,
    paddingBottom: 10,
    zIndex: 1,
  },
  // Gradient scrim anchored to the top of the reserved controls space.
  // `bottom` is set dynamically to contentPaddingBottom (the top of the solid
  // background gap). Extends 64px upward, fading from transparent to the
  // screen background color. Fixed height — never grows with the text area.
  controlsBackgroundGradient: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 32,
  },
  scrubberRow: {
    paddingTop: 8,
    paddingBottom: 20,
  },
  buttonRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 12,
  },
  leftAccessoryWrapper: {
    flexShrink: 0,
  },
  flexSpacer: {
    flex: 1,
  },
  bottomSlotWrapper: {
    flexShrink: 0,
    alignItems: "flex-end",
  },
  bottomSlotFullWidth: {
    flex: 1,
    alignItems: "stretch",
  },
  topAccessoriesRow: {
    position: "absolute",
    flexDirection: "row",
    alignItems: "center",
    zIndex: 3100,
  },
  topRightAccessory: {
    position: "absolute",
    zIndex: 3100,
  },
  topRowSideSlot: {
    // Matches the close button's footprint (36px button + 12px right margin = 48px).
    // The left mirror slot being the same width keeps topCenterAccessory truly centered.
    width: 48,
    alignItems: "flex-end",
  },
  topRowCenter: {
    flex: 1,
    alignItems: "center",
  },
})
